import { defineList, type FieldsOf } from "./define";

/**
 * Schema dell'app "Gare" (sostituisce, per la parte gare e cartelle, la Power App
 * PartecipazioneGare "In Gara 365").
 * Nomi interni ASCII senza accenti; le etichette visibili possono averli.
 *
 * Elementi della vecchia app da NON toccare: Elenco_gareOA, serviziOA, Checklist,
 * Interesse, Cooperative. I titoli qui sotto sono diversi ("Checklist gara", non "Checklist").
 */

/* -------------------------------------------------------------------------- */
/* Valori di riferimento                                                       */
/* -------------------------------------------------------------------------- */

/** Stato della scheda. "Scaduta" non è uno stato: si calcola dalla scadenza. */
export const STATI_GARA = ["Attiva", "Archiviata"] as const;
export type StatoGara = (typeof STATI_GARA)[number];

/** Decisione sulla partecipazione: le cartelle si creano solo con "Partecipa". */
export const VALUTAZIONI = ["Da valutare", "Partecipa", "Non partecipa"] as const;
export type Valutazione = (typeof VALUTAZIONI)[number];

export const ESITI = ["Aggiudicata", "Non aggiudicata"] as const;
export type Esito = (typeof ESITI)[number];

/** Una scheda è una gara oppure l'iscrizione a un albo (elenco fornitori): stesso elenco, stessa numerazione. */
export const TIPI_SCHEDA = ["Gara", "Albo"] as const;
export type TipoScheda = (typeof TIPI_SCHEDA)[number];

/** Tipo di ente che indice la gara: sono le cartelle di primo livello della raccolta Repository. */
export const TIPI_ENTE = [
  "Comuni",
  "Asl",
  "Province",
  "Comuni Associati",
  "Regioni",
  "Enti",
  "Ministeri",
  "Istituti Superiori",
  "Stazioni Uniche Appaltanti",
  "Comunità montane",
  "Camera di commercio",
  "Poste",
] as const;
export type TipoEnte = (typeof TIPI_ENTE)[number];

/** Stati di un documento della checklist (gli stessi della vecchia app). */
export const STATI_DOCUMENTO = ["da fare", "provvisorio", "da rivedere", "definitivo", "approvato"] as const;
export type StatoDocumento = (typeof STATI_DOCUMENTO)[number];

/** Plico (busta) in cui va il documento. */
export const PLICHI = ["Documentazione Amministrativa", "Offerta tecnica", "Offerta economica"] as const;
export type Plico = (typeof PLICHI)[number];

/** Chi partecipa sempre: le mandanti dell'eventuale RTI si indicano sulla gara. */
export const SOGGETTO_PRINCIPALE = "Orizzonte Altri Colori";

/**
 * Sigla come entra nel nome della cartella: maiuscola, spazi e trattini bassi
 * diventano trattini (il trattino basso separa le parti del nome), niente caratteri
 * non ammessi da SharePoint. "CSE CDD" → "CSE-CDD", "dopo_di_noi" → "DOPO-DI-NOI".
 * Sigle uguali per servizi diversi sono ammesse: il numero OA rende unico il nome.
 */
export function normalizzaSigla(sigla: string): string {
  return sigla
    .toUpperCase()
    .replace(/["*:<>?/\\|#%]/g, "")
    .replace(/[\s_]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^[-.]+|[-.]+$/g, "");
}

/* -------------------------------------------------------------------------- */
/* Elenchi                                                                     */
/* -------------------------------------------------------------------------- */

/** Servizi oggetto delle gare: la sigla entra nel nome della cartella. */
export const ServiziGara = defineList({
  key: "servizi",
  displayName: "Servizi gara",
  description: "Servizi oggetto delle gare, con la sigla usata nel nome delle cartelle",
  template: "genericList",
  titleLabel: "Servizio",
  columns: [
    { kind: "text", name: "Sigla", displayName: "Sigla", required: true, maxLength: 50 },
    { kind: "text", name: "Tipologia", displayName: "Tipologia" },
    { kind: "boolean", name: "Attivo", displayName: "Attivo", defaultValue: true },
  ],
});
export type ServizioFields = FieldsOf<typeof ServiziGara>;

export const Gare = defineList({
  key: "gare",
  displayName: "Gare",
  description: "Gare pubbliche censite; le cartelle di gara si creano dall'app",
  template: "genericList",
  titleLabel: "Oggetto",
  indexBuiltIn: ["Created"],
  columns: [
    // --- automatici ---
    {
      kind: "number",
      name: "Numero",
      displayName: "Numero",
      decimalPlaces: "none",
      indexed: true,
      description: "ID dell'elemento + base configurata (la prima gara è OA1000)",
    },
    {
      kind: "text",
      name: "Codice",
      displayName: "Codice",
      maxLength: 100,
      indexed: true,
      description: "OA + numero_sigla del servizio_data di inserimento (es. OA1000_ASS_03-10-2026), assegnato dall'app; è il nome della cartella",
    },
    {
      kind: "choice",
      name: "Tipo",
      displayName: "Tipo",
      choices: TIPI_SCHEDA,
      defaultValue: "Gara",
      indexed: true,
      description: "Gara oppure iscrizione a un albo (vuoto = Gara). Si sceglie alla creazione e non cambia: entra nel codice (_ALBO)",
    },
    // --- compilati dal gestore ---
    {
      kind: "note",
      name: "OggettoCompleto",
      displayName: "Oggetto completo",
      description: "Testo integrale dell'oggetto quando supera i 255 caratteri del Titolo (che ne contiene l'inizio)",
    },
    { kind: "text", name: "Stazione", displayName: "Stazione appaltante", required: true },
    {
      kind: "choice",
      name: "TipoEnte",
      displayName: "Tipo di ente",
      choices: TIPI_ENTE,
      description: "Proposto dalla posizione della cartella di gara (primo livello della raccolta)",
    },
    { kind: "lookup", name: "Servizio", displayName: "Servizio", list: "servizi", required: true, showField: "Sigla" },
    { kind: "text", name: "CIG", displayName: "CIG", maxLength: 40, description: "Per le gare a lotti il CIG è indicato su ciascun lotto" },
    { kind: "currency", name: "Importo", displayName: "Importo a base d'asta", locale: "it-IT" },
    { kind: "dateTime", name: "Scadenza", displayName: "Scadenza presentazione", format: "dateTime", indexed: true },
    { kind: "dateTime", name: "ScadenzaInteresse", displayName: "Data limite manifestazione di interesse", format: "dateTime" },
    { kind: "dateTime", name: "AperturaBuste", displayName: "Apertura buste", format: "dateTime" },
    { kind: "note", name: "LinkBando", displayName: "Link al bando" },
    { kind: "dateTime", name: "DataIscrizione", displayName: "Data iscrizione", format: "dateOnly", description: "Solo albi: vuota = iscrizione non ancora ottenuta" },
    { kind: "dateTime", name: "ScadenzaIscrizione", displayName: "Scadenza / rinnovo iscrizione", format: "dateOnly", description: "Solo albi" },
    { kind: "boolean", name: "PerLotti", displayName: "Gara a lotti", defaultValue: false },
    {
      kind: "text",
      name: "Mandanti",
      displayName: "Altri componenti RTI",
      description: "Partecipazione in RTI: le altre imprese, separate da punto e virgola. Vuoto = Orizzonte Altri Colori da sola",
    },
    { kind: "choice", name: "Valutazione", displayName: "Valutazione", choices: VALUTAZIONI, defaultValue: "Da valutare", indexed: true },
    { kind: "note", name: "NoteValutazione", displayName: "Note di valutazione" },
    { kind: "choice", name: "Stato", displayName: "Stato", choices: STATI_GARA, defaultValue: "Attiva", required: true, indexed: true },
    { kind: "choice", name: "Esito", displayName: "Esito gara", choices: ESITI, description: "Solo per le gare senza lotti: per quelle a lotti l'esito è su ciascun lotto" },
    { kind: "currency", name: "ImportoAggiudicato", displayName: "Importo aggiudicato", locale: "it-IT", description: "Solo per le gare senza lotti" },
    // --- cartelle (scritti dall'app a creazione riuscita) ---
    { kind: "text", name: "NomeCartella", displayName: "Nome cartella" },
    {
      kind: "text",
      name: "CartellaId",
      displayName: "Id cartella",
      description: "Id della cartella nella raccolta (resta valido anche se la cartella viene rinominata o spostata)",
    },
    {
      kind: "note",
      name: "CartellaUrl",
      displayName: "Cartella",
      description: "Gara senza lotti: la cartella di gara. Gara a lotti: la cartella che contiene i lotti",
    },
    {
      kind: "text",
      name: "PosizioneId",
      displayName: "Id posizione",
      description: "Cartella scelta per la creazione: una creazione interrotta riprende nello stesso punto",
    },
    { kind: "note", name: "Posizione", displayName: "Posizione cartella", description: "Percorso nella raccolta, per consultazione" },
  ],
});
export type GaraFields = FieldsOf<typeof Gare>;

export const Lotti = defineList({
  key: "lotti",
  displayName: "Lotti gara",
  description: "Lotti delle gare: ciascuno ha la propria cartella",
  template: "genericList",
  titleLabel: "Oggetto del lotto",
  columns: [
    { kind: "lookup", name: "Gara", displayName: "Gara", list: "gare", required: true, indexed: true, showField: "Codice" },
    { kind: "number", name: "Numero", displayName: "Numero lotto", decimalPlaces: "none", min: 1, required: true },
    { kind: "text", name: "CIG", displayName: "CIG", maxLength: 40 },
    { kind: "currency", name: "Importo", displayName: "Importo", locale: "it-IT" },
    { kind: "choice", name: "Esito", displayName: "Esito lotto", choices: ESITI },
    { kind: "currency", name: "ImportoAggiudicato", displayName: "Importo aggiudicato", locale: "it-IT" },
    { kind: "text", name: "NomeCartella", displayName: "Nome cartella" },
    { kind: "text", name: "CartellaId", displayName: "Id cartella" },
    { kind: "note", name: "CartellaUrl", displayName: "Cartella" },
  ],
});
export type LottoFields = FieldsOf<typeof Lotti>;

/**
 * Documenti che si chiedono di solito: da qui si prepara la checklist di ogni gara
 * (o lotto). I gestori lo modificano dall'app. Dati iniziali ricavati dalla
 * Checklist della vecchia app.
 */
export const ModelloChecklist = defineList({
  key: "modelloChecklist",
  displayName: "Modello checklist",
  description: "Documenti proposti nella checklist di ogni gara",
  template: "genericList",
  titleLabel: "Documento",
  columns: [
    { kind: "choice", name: "Plico", displayName: "Plico", choices: PLICHI, required: true, defaultValue: "Documentazione Amministrativa" },
    { kind: "number", name: "Ordine", displayName: "Ordine", decimalPlaces: "none" },
    {
      kind: "boolean",
      name: "PerComponente",
      displayName: "Uno per componente",
      defaultValue: false,
      description: "In RTI il documento si ripete per ciascun componente",
    },
    { kind: "boolean", name: "SoloRTI", displayName: "Solo in RTI", defaultValue: false, description: "Proposto solo quando si partecipa in RTI" },
    { kind: "boolean", name: "Predefinito", displayName: "Proposto", defaultValue: true, description: "Già selezionato quando si prepara una checklist" },
    { kind: "boolean", name: "Attivo", displayName: "Attivo", defaultValue: true },
  ],
  seed: modelloIniziale(),
});
export type ModelloChecklistFields = FieldsOf<typeof ModelloChecklist>;

function modelloIniziale() {
  const A = "Documentazione Amministrativa";
  // [documento, plico, per componente, solo RTI, proposto]
  const righe: Array<[string, Plico, boolean, boolean, boolean]> = [
    ["Istanza di partecipazione (Mod. 1)", A, true, false, true],
    ["DGUE (PDF)", A, true, false, true],
    ["DGUE (XML)", A, true, false, true],
    ["Dichiarazioni integrative", A, true, false, true],
    ["Patto di integrità", A, true, false, true],
    ["Certificazione di qualità", A, true, false, true],
    ["Dichiarazione titolare effettivo", A, true, false, true],
    ["Dichiarazione conflitto di interesse", A, true, false, true],
    ["Rapporto sulla parità di genere", A, true, false, true],
    ["Atto costitutivo", A, true, false, false],
    ["Statuto", A, true, false, false],
    ["Iscrizione albo regionale cooperative sociali", A, true, false, false],
    ["Iscrizione albo società cooperative", A, true, false, false],
    ["PassOE", A, false, false, true],
    ["Contributo ANAC", A, false, false, true],
    ["Cauzione provvisoria", A, false, false, true],
    ["Procura dell'assicuratore", A, false, false, false],
    ["Dichiarazione di esenzione dal bollo", A, false, false, true],
    ["Atto di impegno a costituire RTI", A, false, true, true],
    ["Offerta tecnica", "Offerta tecnica", false, false, true],
    ["Dichiarazione segreti tecnici e commerciali", "Offerta tecnica", false, false, true],
    ["Offerta economica", "Offerta economica", false, false, true],
    ["Offerta economica generata dalla piattaforma", "Offerta economica", false, false, false],
  ];
  return righe.map(([Title, Plico, PerComponente, SoloRTI, Predefinito], i) => ({
    Title,
    Plico,
    Ordine: (i + 1) * 10,
    PerComponente,
    SoloRTI,
    Predefinito,
    Attivo: true,
  }));
}

/**
 * Checklist dei documenti: una per gara senza lotti, una per lotto nelle gare a lotti
 * (Lotto vuoto = gara senza lotti).
 */
export const ChecklistGara = defineList({
  key: "checklist",
  displayName: "Checklist gara",
  description: "Documenti da preparare per ciascuna gara o lotto",
  template: "genericList",
  titleLabel: "Documento",
  columns: [
    { kind: "lookup", name: "Gara", displayName: "Gara", list: "gare", required: true, indexed: true, showField: "Codice" },
    { kind: "lookup", name: "Lotto", displayName: "Lotto", list: "lotti", showField: "Title" },
    { kind: "choice", name: "Plico", displayName: "Plico", choices: PLICHI, required: true, defaultValue: "Documentazione Amministrativa" },
    { kind: "text", name: "Soggetto", displayName: "Soggetto", description: "Componente del raggruppamento a cui si riferisce il documento" },
    { kind: "choice", name: "Stato", displayName: "Stato", choices: STATI_DOCUMENTO, required: true, defaultValue: "da fare" },
    { kind: "number", name: "Ordine", displayName: "Ordine", decimalPlaces: "none" },
    { kind: "text", name: "Note", displayName: "Note" },
    { kind: "text", name: "FileNome", displayName: "File" },
    { kind: "text", name: "FileId", displayName: "Id file" },
    { kind: "note", name: "FileUrl", displayName: "Collegamento al file" },
  ],
});
export type ChecklistFields = FieldsOf<typeof ChecklistGara>;

/**
 * Raccolta esistente che contiene le cartelle di gara. Il provisioning non la crea:
 * aggiunge solo le colonne, che l'app compila sulle cartelle che crea.
 */
export const Repository = defineList({
  key: "repository",
  displayName: "Repository",
  template: "documentLibrary",
  existing: true,
  columns: [
    { kind: "lookup", name: "Gara", displayName: "Gara", list: "gare", indexed: true, showField: "Codice" },
    { kind: "number", name: "Lotto", displayName: "Lotto", decimalPlaces: "none" },
  ],
});
export type RepositoryFields = FieldsOf<typeof Repository>;

/** Tutte le strutture dati dell'app, nell'ordine di provisioning (i lookup dopo i loro elenchi). */
export const appSchema = [ServiziGara, Gare, Lotti, Repository, ModelloChecklist, ChecklistGara] as const;
